import { create } from "zustand";
import { getCsrfToken } from "../state/csrf";
import { ParcelResearch, ProjectLink, ResearchChoice } from "./types";

const json = (method: string, body?: any): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
  ...(body ? { body: JSON.stringify(body) } : {}),
});

type ResearchState = {
  projectId?: number;
  researches: ParcelResearch[];
  projectLinks: ProjectLink[];
  detail?: ParcelResearch;
  loading: boolean;
  error?: string;
  completedNotice?: ParcelResearch;
  openProject: (projectId: number) => void;
  load: () => Promise<ParcelResearch[]>;
  loadDetail: (id: number) => Promise<ParcelResearch | undefined>;
  start: (
    parcelId: number,
    choice: ResearchChoice,
    links?: { chatSessionId?: number; proposalId?: number; previousVersionId?: number }
  ) => Promise<number | null>;
  cancel: (id: number) => Promise<void>;
  retry: (id: number) => Promise<number | null>;
  sourceAction: (researchId: number, sourceId: number, action: string) => Promise<void>;
  clearNotice: () => void;
};

export const useResearch = create<ResearchState>((set, get) => ({
  researches: [],
  projectLinks: [],
  loading: false,

  openProject: (projectId) => {
    if (get().projectId !== projectId)
      set({ projectId, researches: [], projectLinks: [], detail: undefined });
  },

  load: async () => {
    const { projectId, researches: previous } = get();
    if (!projectId) return [];
    const [response, linksResponse] = await Promise.all([
      fetch(`/api/projects/${projectId}/research`),
      fetch(`/api/projects/${projectId}/links`),
    ]);
    if (!response.ok) return previous;
    const researches: ParcelResearch[] = await response.json();
    const projectLinks: ProjectLink[] = linksResponse.ok ? await linksResponse.json() : [];
    const completed = researches.find(
      (item) =>
        item.status === "completed" &&
        previous.some((old) => old.id === item.id && old.status !== "completed")
    );
    set({ researches, projectLinks, completedNotice: completed ?? get().completedNotice });
    return researches;
  },

  loadDetail: async (id) => {
    const { projectId } = get();
    if (!projectId) return undefined;
    set({ loading: true, error: undefined });
    const response = await fetch(`/api/projects/${projectId}/research/${id}`);
    if (!response.ok) {
      set({ loading: false, error: "Kon onderzoek niet laden" });
      return undefined;
    }
    const detail: ParcelResearch = await response.json();
    set({ detail, loading: false });
    return detail;
  },

  start: async (parcelId, choice, links = {}) => {
    const { projectId } = get();
    if (!projectId) return null;
    set({ loading: true, error: undefined });
    const { question, ...setup } = choice;
    const response = await fetch(
      `/api/projects/${projectId}/research`,
      json("POST", { parcelIds: [parcelId], extraQuestion: question, ...setup, ...links })
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      set({ loading: false, error: data.detail || "Onderzoek starten mislukt" });
      return null;
    }
    const detail: ParcelResearch = await response.json();
    set({ detail, loading: false });
    await get().load();
    return detail.id;
  },

  cancel: async (id) => {
    const { projectId } = get();
    if (!projectId) return;
    await fetch(`/api/projects/${projectId}/research/${id}/cancel`, json("POST"));
    await get().load();
    if (get().detail?.id === id) await get().loadDetail(id);
  },

  retry: async (id) => {
    const { projectId } = get();
    if (!projectId) return null;
    const response = await fetch(`/api/projects/${projectId}/research/${id}/retry`, json("POST"));
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      set({ error: data.detail || "Opnieuw starten mislukt" });
      return null;
    }
    const detail: ParcelResearch = await response.json();
    set({ detail });
    await get().load();
    return detail.id;
  },

  sourceAction: async (researchId, sourceId, action) => {
    const { projectId } = get();
    if (!projectId) return;
    set({ error: undefined });
    const response = await fetch(
      `/api/projects/${projectId}/research/${researchId}/sources/${sourceId}/${action}`,
      json("POST")
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      set({ error: data.detail || "Bron bijwerken mislukt" });
      return;
    }
    await get().loadDetail(researchId);
    await get().load();
  },

  clearNotice: () => set({ completedNotice: undefined }),
}));
